import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { useFormik } from 'formik';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isValidEmail } from 'src/utils/strings';
import * as Yup from 'yup';

export interface UserInfoSsoFormValues {
  ssoEmail: string;
  confirmSsoEmail: string;
}

interface UseUserInfoSsoFormProps {
  displayEmail: string;
  setDisplayEmail: (email: string) => void;
  onSaveEmail?: (email: string) => Promise<void>;
  t: TFunction;
}

interface UseUserInfoSsoFormResult {
  formik: ReturnType<typeof useFormik<UserInfoSsoFormValues>>;
  isSsoEditing: boolean;
  handleSsoEditClick: () => void;
  handleSsoSaveClick: () => void;
  handleSsoCancelClick: () => void;
  createFormikChangeHandler: (e: TextBoxChangeEvent) => void;
  validationMessages: string[];
  isSsoSaveEnabled: boolean;
}

const hasInvalidDoubleQuote = (email: string) => email.includes('"');

const isValidSsoEmail = (email: string) =>
  isValidEmail(email) && !hasInvalidDoubleQuote(email);

const createValidationSchema = (t: TFunction) =>
  Yup.object<UserInfoSsoFormValues>({
    ssoEmail: Yup.string()
      .trim()
      .required(t('invalidNotificationEmailAddress'))
      .test(
        'valid-email',
        t('invalidNotificationEmailAddress'),
        (value) => isValidSsoEmail((value ?? '').trim())
      ),
    confirmSsoEmail: Yup.string()
      .trim()
      .test(
        'match',
        t('ssoEmailsDoNotMatch'),
        function (value) {
          const sso = (this.parent as UserInfoSsoFormValues).ssoEmail?.trim() ?? '';
          const confirm = (value ?? '').trim();
          if (confirm === '') return true;
          return sso === confirm;
        }
      ),
  });

const useUserInfoSsoForm = ({
  displayEmail,
  setDisplayEmail,
  onSaveEmail,
  t,
}: UseUserInfoSsoFormProps): UseUserInfoSsoFormResult => {
  const [isSsoEditing, setIsSsoEditing] = useState(false);

  const initialValues: UserInfoSsoFormValues = useMemo(
    () => ({
      ssoEmail: displayEmail,
      confirmSsoEmail: '',
    }),
    [displayEmail]
  );

  const validationSchema = useMemo(() => createValidationSchema(t), [t]);

  const formik = useFormik<UserInfoSsoFormValues>({
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: () => {
      // Handled in handleSsoSaveClick
    },
  });

  const { resetForm } = formik;

  useEffect(() => {
    if (isSsoEditing) {
      resetForm({
        values: {
          ssoEmail: displayEmail,
          confirmSsoEmail: '',
        },
      });
    }
  }, [isSsoEditing, displayEmail, resetForm]);

  const handleSsoEditClick = useCallback(() => {
    setIsSsoEditing(true);
  }, []);

  const handleSsoSaveClick = useCallback(async () => {
    const trimmed = formik.values.ssoEmail.trim();
    if (
      !trimmed ||
      trimmed !== formik.values.confirmSsoEmail.trim() ||
      !isValidSsoEmail(formik.values.ssoEmail)
    ) {
      return;
    }
    if (onSaveEmail) {
      await onSaveEmail(trimmed);
    }
    setDisplayEmail(trimmed);
    setIsSsoEditing(false);
    resetForm({
      values: {
        ssoEmail: trimmed,
        confirmSsoEmail: '',
      },
    });
  }, [
    formik.values.ssoEmail,
    formik.values.confirmSsoEmail,
    setDisplayEmail,
    resetForm,
    onSaveEmail,
  ]);

  const handleSsoCancelClick = useCallback(() => {
    setIsSsoEditing(false);
    resetForm({ values: initialValues });
  }, [initialValues, resetForm]);

  const createFormikChangeHandler = useCallback(
    (e: TextBoxChangeEvent) => {
      const fieldName = e.syntheticEvent.target.name as keyof UserInfoSsoFormValues;
      const value = String(e.value ?? '');
      const syntheticEvent = {
        target: {
          name: fieldName,
          id: fieldName,
          value,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      formik.handleChange(syntheticEvent);
      formik.setFieldTouched(fieldName, true, false);
      formik.setFieldValue(fieldName, value, true);
    },
    [formik]
  );

  const validationMessages = useMemo(
    () => Object.values(formik.errors).filter((error): error is string => Boolean(error)),
    [formik.errors]
  );

  const isSsoSaveEnabled =
    formik.values.confirmSsoEmail.trim() !== '' &&
    formik.values.ssoEmail.trim() === formik.values.confirmSsoEmail.trim() &&
    isValidSsoEmail(formik.values.ssoEmail);

  return {
    formik,
    isSsoEditing,
    handleSsoEditClick,
    handleSsoSaveClick,
    handleSsoCancelClick,
    createFormikChangeHandler,
    validationMessages,
    isSsoSaveEnabled,
  };
};

export default useUserInfoSsoForm;
